/**
 * Shared utilities for ERC (Electrical Rule Check) validators
 *
 * This module provides common interfaces and helper functions used across
 * all ERC validators to reduce code duplication and improve maintainability.
 */
import { defaultPropertyDefinitions, portalPartType } from "@buildwithflux/constants";
import { EditorModes, formatPinReference as formatPinReferenceShared, getElementPartCategoryProperty, getElementPartType, isGroundElement, } from "@buildwithflux/models";
import { fluxPinTypeToElectricalType } from "../pinTypes";
/**
 * Gets the effective pin type for a terminal, checking the user-modified
 * properties first, then falling back to terminal.type.
 *
 * When a user modifies the "Pin Type" property in the UI, it's stored in
 * terminal.properties[pin_type_key].value, not in terminal.type (which is the
 * cached value from the component definition).
 *
 * @param terminal - The terminal data to get the pin type from
 * @returns The effective pin type string, or undefined if not set
 */
export function getEffectivePinType(terminal) {
    // Check if the user has overridden the pin type via properties
    // Use defaultPropertyDefinitions.pin_type.key to access the property by its standard key
    const pinTypeKey = defaultPropertyDefinitions.pin_type.key;
    const pinTypeProperty = terminal.properties?.[pinTypeKey];
    if (pinTypeProperty && typeof pinTypeProperty.value === "string") {
        return pinTypeProperty.value;
    }
    // Fall back to the cached type from the component definition
    return terminal.type;
}
/**
 * Parses a terminal ID string into its component parts.
 *
 * Terminal IDs are in the format "elementUid__terminalUid" (double underscore delimiter).
 * This format is created by createTUID from @buildwithflux/models.
 *
 * @param terminalId - The terminal ID string to parse
 * @returns The parsed element and terminal UIDs, or null if invalid
 */
export function parseTerminalId(terminalId) {
    const parts = terminalId.split("__");
    if (parts.length < 2) {
        return null;
    }
    const elementUid = parts[0];
    const terminalUid = parts.slice(1).join("__");
    return { elementUid, terminalUid };
}
/**
 * Checks if an element is a portal (Net Portal, Power Net Portal, etc.).
 * Portals connect nets across the schematic and intentionally share labels.
 *
 * @param element - The element to check
 * @returns True if the element is a portal
 */
export function isPortal(element) {
    return getElementPartCategoryProperty(element)?.value === portalPartType;
}
/**
 * Checks if an element is a portal or ground element.
 * These elements are typically excluded from reference designator checks
 * and power pin validation.
 *
 * @param element - The element to check
 * @returns True if the element is a portal or ground
 */
export function isPortalOrGroundElement(element) {
    return isPortal(element) || isGroundElement(element);
}
/**
 * Checks if a Part Type value indicates a power source component.
 * This includes:
 * - "Power Source" (exact match, case-insensitive) - system predefined value
 * - Any part type containing "battery" (case-insensitive, fuzzy match) - covers "Battery", "Battery Connector", etc.
 *
 * @param partType - The Part Type property value to check
 * @returns True if the part type indicates a power source
 */
export function isPowerSourcePartType(partType) {
    if (!partType)
        return false;
    const lowerPartType = partType.toLowerCase();
    return lowerPartType === "power source" || lowerPartType.includes("battery");
}
/**
 * Checks if a Part Type value indicates an inductor.
 * Inductors pass power through (e.g. LC filters, power inductor in series).
 *
 * @param partType - The Part Type property value to check
 * @returns True if the part type indicates an inductor
 */
export function isInductorPartType(partType) {
    if (!partType)
        return false;
    return partType.toLowerCase() === "inductor";
}
/**
 * Pin name patterns that indicate a positive/power output terminal.
 * Used to identify which pins on a power source component can drive power inputs.
 * Note: "OUT" is intentionally excluded as it's too broad and would match non-power pins
 * like "DOUT" (data output), "COUT" (counter output), "OUTPUT", etc.
 */
const POSITIVE_PIN_PATTERNS = ["+", "V+", "VOUT", "VCC", "VDD", "VBAT", "VBUS", "VS+", "POS", "POSITIVE"];
/**
 * Pin name patterns that indicate a negative/ground terminal.
 * These patterns are checked first to prevent ground pins from being incorrectly
 * identified as positive when they happen to be pin number "1".
 */
const NEGATIVE_PIN_PATTERNS = ["-", "GND", "VSS", "V-", "NEG", "NEGATIVE", "GROUND"];
/**
 * Checks if a pin name matches negative/ground terminal patterns.
 *
 * @param upperName - The pin name in uppercase, trimmed
 * @returns True if the pin appears to be a negative/ground terminal
 */
function isNegativePin(upperName) {
    for (const pattern of NEGATIVE_PIN_PATTERNS) {
        if (upperName === pattern || upperName.startsWith(pattern)) {
            return true;
        }
    }
    return false;
}
/**
 * Checks if a pin is likely a positive/power output terminal of a power source.
 * This is used to determine which pins on battery/power source components
 * should be treated as power drivers.
 *
 * A pin is considered a positive terminal if:
 * - Its name matches common positive terminal patterns (case-insensitive)
 * - Its name does NOT match negative/ground patterns
 * - OR its pin number is "1" AND it has no name (common convention for positive terminal on batteries)
 *
 * Note: The pin number "1" heuristic is only used when there's no pin name,
 * to avoid false positives for pins like "EN" (enable) that happen to be pin 1.
 *
 * @param pinName - The pin's name
 * @param pinNumber - The pin's number
 * @returns True if the pin appears to be a positive/power output terminal
 */
export function isLikelyPositivePin(pinName, pinNumber) {
    // Check pin name against patterns
    if (pinName) {
        const upperName = pinName.toUpperCase().trim();
        // First check if this is a negative/ground pin - if so, it's definitely not positive
        if (isNegativePin(upperName)) {
            return false;
        }
        // Check for positive patterns
        for (const pattern of POSITIVE_PIN_PATTERNS) {
            if (upperName === pattern || upperName.startsWith(pattern)) {
                return true;
            }
        }
        // Pin has a name but doesn't match any positive pattern - don't use pin number heuristic
        return false;
    }
    // Pin 1 is commonly the positive terminal on batteries and power connectors,
    // but only use this heuristic when there's no pin name to check
    if (pinNumber === "1") {
        return true;
    }
    return false;
}
/**
 * Gets pin information for a specific terminal from the DRC inputs.
 *
 * @param inputs - The DRC inputs containing elements and their terminals
 * @param terminalId - The terminal ID to look up
 * @returns Pin information, or null if the terminal doesn't exist
 */
export function getPinInfo(inputs, terminalId) {
    const parsed = parseTerminalId(terminalId);
    if (!parsed) {
        return null;
    }
    const { elementUid, terminalUid } = parsed;
    const element = inputs.elements[elementUid];
    if (!element) {
        return null;
    }
    const terminal = element.part_version_data_cache?.terminals?.[terminalUid];
    if (!terminal) {
        return null;
    }
    // Use getEffectivePinType to respect user-modified pin types from properties
    const pinType = fluxPinTypeToElectricalType(getEffectivePinType(terminal));
    const pinName = terminal.name;
    const pinNumber = terminal.pin_number;
    const elementLabel = element.label;
    const elementName = element.part_version_data_cache?.name;
    const elementPartType = getElementPartType(element);
    return {
        elementUid,
        terminalUid,
        terminalId,
        pinType,
        pinName,
        pinNumber,
        elementLabel,
        elementName,
        elementPartType,
    };
}
/**
 * Gets all pin information for terminals in a net.
 *
 * @param inputs - The DRC inputs containing elements and their terminals
 * @param terminals - Object with terminal IDs as keys (from netMap)
 * @returns Array of pin information for all valid terminals in the net
 */
export function getPinsForNet(inputs, terminals) {
    const pins = [];
    for (const terminalId of Object.keys(terminals)) {
        const pinInfo = getPinInfo(inputs, terminalId);
        if (pinInfo) {
            pins.push(pinInfo);
        }
    }
    return pins;
}
/**
 * Creates an ERC problem with standard structure.
 *
 * @param problemTypeKey - The problem type key for this validator
 * @param key - Unique key for this specific problem instance
 * @param label - Human-readable description of the problem
 * @param affectedElementUids - Array of element UIDs affected by this problem
 * @param itemLabels - Optional array of labels for affected items (parallel to affectedElementUids)
 * @param severity - Optional severity level (defaults to ERROR for backward compatibility)
 * @param metadata - Optional additional metadata (e.g. ercNet for net column)
 * @returns A properly structured IProblem object
 */
export function createErcProblem(problemTypeKey, key, label, affectedElementUids, itemLabels, severity, metadata) {
    const baseMetadata = severity ? { ercSeverity: severity } : undefined;
    const mergedMetadata = baseMetadata || metadata ? { ...(baseMetadata ?? {}), ...(metadata ?? {}) } : undefined;
    return {
        problemTypeKey,
        key,
        label,
        affectedItems: affectedElementUids.map((uid, index) => ({
            type: "element",
            uid,
            itemLabel: itemLabels?.[index],
        })),
        affectedViews: [EditorModes.schematic],
        metadata: mergedMetadata,
    };
}
/**
 * Formats a pin reference for display in error messages.
 * Uses the standard partDesignator:pinDesignator syntax (e.g., "U2:EN", "R1:1").
 * Gracefully degrades to pinNumber when pinName is empty/null.
 * Falls back to component name when designator is missing.
 *
 * @param pin - The pin information
 * @returns A formatted string like "U2:EN" or "R1:1" or "LM7805:VIN"
 */
export function formatPinReference(pin) {
    return formatPinReferenceShared(pin.elementLabel, pin.pinName, pin.pinNumber, pin.elementName);
}
//# sourceMappingURL=ercUtils.js.map